import type { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "../visuallyHidden";

export interface MatchCardPlayer {
  name: string;
  /** "#handle" when the source supplies one; omitted otherwise. */
  handle?: string | null;
}

export interface MatchCardSide {
  /** One entry per player: doubles teams stack one player per line. */
  players: readonly MatchCardPlayer[];
  /** Text shown instead of players (e.g. "Bye", "TBD"). */
  placeholder?: string;
  won?: boolean;
  /** This side's points per game, in order. */
  scores?: readonly (number | string)[];
}

/**
 * The approved match card used by draws, schedule, results and the drop-in court
 * board: a one-line header (status, time, court; a red LIVE marker while in
 * play), one block per side with each player on their own line, and game scores
 * in fixed right-aligned columns. The winning side is bold; the viewer's own
 * matches are outlined in the accent colour.
 */
export function MatchCard({
  header,
  live = false,
  mine = false,
  sides,
  ariaLabel,
  footer,
}: {
  header: ReactNode;
  live?: boolean;
  mine?: boolean;
  sides: readonly [MatchCardSide, MatchCardSide];
  ariaLabel?: string;
  footer?: ReactNode;
}) {
  const columns = Math.max(sides[0].scores?.length ?? 0, sides[1].scores?.length ?? 0);
  const decided = sides.some((side) => side.won);
  return (
    <Box
      component="article"
      aria-label={ariaLabel}
      data-mine={mine || undefined}
      sx={{
        bgcolor: "background.paper",
        border: mine ? "1.5px solid" : "1px solid",
        borderColor: mine ? "primary.main" : "divider",
        borderRadius: "12px",
        px: 1.25,
        py: 1,
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: "center", mb: 0.5, minWidth: 0 }}
      >
        {live ? (
          <Box
            aria-hidden
            sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "error.main", flexShrink: 0 }}
          />
        ) : null}
        <Typography
          noWrap
          sx={{
            fontSize: "0.75rem",
            color: live ? "error.main" : "text.secondary",
            minWidth: 0,
          }}
        >
          {live ? "LIVE · " : null}
          {header}
        </Typography>
      </Stack>
      {sides.map((side, index) => (
        <Stack
          key={index}
          direction="row"
          spacing={1}
          role={side.won ? "group" : undefined}
          aria-label={side.won ? "Winner" : undefined}
          data-won={side.won || undefined}
          sx={{
            alignItems: "center",
            py: 0.5,
            minWidth: 0,
            ...(index === 1 ? { borderTop: 1, borderColor: "divider" } : {}),
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {side.players.length > 0 ? (
              side.players.map((player, playerIndex) => (
                <Typography
                  key={playerIndex}
                  noWrap
                  sx={{
                    fontSize: "0.875rem",
                    lineHeight: 1.35,
                    fontWeight: side.won ? 700 : 400,
                    color: decided && !side.won ? "text.secondary" : "text.primary",
                  }}
                >
                  {player.name}
                  {player.handle ? (
                    <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                      {" "}
                      {player.handle}
                    </Box>
                  ) : null}
                </Typography>
              ))
            ) : (
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                {side.placeholder ?? "TBD"}
              </Typography>
            )}
          </Box>
          {columns > 0 ? (
            <Stack direction="row" aria-hidden sx={{ flexShrink: 0 }}>
              {Array.from({ length: columns }, (_, game) => (
                <Typography
                  key={game}
                  component="span"
                  sx={{
                    width: 26,
                    textAlign: "right",
                    fontSize: "0.875rem",
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: side.won ? 800 : 400,
                    color: decided && !side.won ? "text.secondary" : "text.primary",
                  }}
                >
                  {side.scores?.[game] ?? ""}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Stack>
      ))}
      {columns > 0 ? (
        // The score columns read as one phrase for assistive tech.
        <Box component="span" sx={visuallyHidden}>
          {`Score: ${Array.from(
            { length: columns },
            (_, game) =>
              `${sides[0].scores?.[game] ?? ""}–${sides[1].scores?.[game] ?? ""}`,
          ).join(", ")}`}
        </Box>
      ) : null}
      {footer}
    </Box>
  );
}
